import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, Loader2, ShieldCheck, DollarSign, Scale } from "lucide-react";
import { RETAILERS, getRetailer } from "@/lib/retailers";
import { guidedSearch, getGuidedSearchStatus, getVault, saveToVault, removeFromVault } from "@/lib/api";
import { getTrustBadge, guessManufacturer } from "@/features/snaggedify/dealFeedData";
import { SafeImg } from "@/components/SafeImg";
import PdpModal from "@/features/snaggedify/PdpModal";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/owner";
import { planToTier } from "@/lib/tierGate";
import { toast } from "@/components/ui/sonner";
import { SignInModal } from "@/components/SignInModal";

const ROLE_META = {
  BEST_PRICE: { label: "Best Price", Icon: DollarSign, color: "text-emerald-600 dark:text-emerald-400" },
  BEST_VALUE: { label: "Best Value", Icon: Scale, color: "text-brand-orange dark:text-amber-400" },
  SAFEST_CHOICE: { label: "Safest Choice", Icon: ShieldCheck, color: "text-sky-600 dark:text-sky-400" },
};

// Retailer-Themed Search Workspace — Section 2/3. STRICTLY scoped to one retailer's
// own domain; the 3-Option Intent Engine returns at most Best Price / Best Value /
// Safest Choice, gated by its own daily credit pool (separate from Check Link, which
// stays free everywhere, and from Daily Recommendations).
export default function RetailerSearchPage() {
  const { retailerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const retailer = getRetailer(retailerId);
  const currentTier = isOwner(user) ? "ELITE" : planToTier(user?.plan);

  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [mode, setMode] = useState("general");
  const [attrs, setAttrs] = useState({ model: "", capacity: "", condition: "", color: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [credit, setCredit] = useState(null);
  const [pdpDeal, setPdpDeal] = useState(null);
  const [vaultIds, setVaultIds] = useState(new Set());
  const [showSignIn, setShowSignIn] = useState(false);
  const isGuest = !!user?.is_guest;

  useEffect(() => { getGuidedSearchStatus().then(setCredit).catch(() => {}); }, [retailerId]);
  useEffect(() => {
    if (!user || user.is_guest) return;
    getVault().then((res) => setVaultIds(new Set((res.items || []).map((it) => it.deal_id)))).catch(() => {});
  }, [user?.user_id]);

  const guardedToggleSave = async (deal) => {
    if (!deal) return;
    if (isGuest) { setShowSignIn(true); return; }
    if (vaultIds.has(deal.id)) {
      setVaultIds((prev) => { const next = new Set(prev); next.delete(deal.id); return next; });
      removeFromVault(deal.id).catch(() => {});
      return;
    }
    try {
      const res = await saveToVault({
        deal_id: deal.id, title: deal.title, price: deal.price, msrp: deal.msrp,
        verdict: deal.verdict, image: deal.image || "", reseller: deal.reseller || "", outbound_url: deal.outboundUrl || "",
        fulfillment_mode: deal.fulfillment || "ONLINE_SHIPPING",
      });
      if (!res.already_saved) setVaultIds((prev) => new Set(prev).add(deal.id));
    } catch (err) {
      if (err?.status === 402) { navigate("/pricing"); return; }
      toast.error("Couldn't save — try again.");
    }
  };

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await guidedSearch({ retailerId, query: query.trim(), mode, attributes: attrs });
      setResults(res.results || []);
      setIsDemo(res.source === "seed");
      setCredit(res.credit_status || null);
    } catch (err) {
      if (err?.status === 402) {
        setError(err?.data?.message || "You've used today's retailer searches — come back after the reset or upgrade.");
        setCredit({ limit: err?.data?.limit, remaining: 0, reset_at: err?.data?.reset_at });
      } else {
        setError(err?.message || "Search is temporarily unavailable — try again shortly.");
      }
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const openResult = (r) => {
    setPdpDeal({
      id: r.id, title: r.title, price: r.price, msrp: r.msrp_baseline ?? r.price,
      image: r.image || "", reseller: r.reseller, manufacturer: guessManufacturer(r.title || ""),
      verdict: (r.verdict || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", ""),
      outboundUrl: r.outbound_url, fulfillment: r.fulfillment_mode || "ONLINE_SHIPPING",
    });
  };

  return (
    <div data-testid="retailer-search-page" className="min-h-screen supports-[height:100dvh]:min-h-[100dvh]" style={{ backgroundColor: retailer.primary }}>
      <header className="flex items-center gap-3 px-4 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
        <button onClick={() => navigate("/app")} data-testid="retailer-back-btn" aria-label="Back to Dashboard" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-black text-white" data-testid="retailer-name">{retailer.name}</h1>
          <p className="truncate text-[11px] font-bold uppercase tracking-wide text-white/70" data-testid="retailer-trust-signal">
            Verified {retailer.name} listings only · {retailer.fulfillment === "IN_STORE_PICKUP" ? "Local Pickup" : "Online Shipping"}
          </p>
        </div>
        {credit && (
          <span data-testid="retailer-credit-counter" className="shrink-0 rounded-full bg-white/15 px-3 py-1.5 text-xs font-black text-white">
            {credit.limit == null ? "∞" : `${credit.remaining}/${credit.limit}`} today
          </span>
        )}
      </header>

      <div className="flex gap-2 overflow-x-auto px-4 pb-3" data-testid="retailer-switcher">
        {RETAILERS.map((r) => (
          <button
            key={r.id}
            onClick={() => navigate(`/search/${r.id}${query ? `?q=${encodeURIComponent(query)}` : ""}`)}
            data-testid={`retailer-switch-${r.id}`}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold ${r.id === retailerId ? "bg-white text-slate-900" : "bg-white/15 text-white"}`}
          >
            {r.name}
          </button>
        ))}
      </div>

      <main className="min-h-[70vh] rounded-t-3xl bg-slate-50 px-4 pb-28 pt-5 dark:bg-charcoal">
        <div className="flex gap-2" data-testid="retailer-mode-toggle">
          <button onClick={() => setMode("general")} data-testid="retailer-mode-general" className={`rounded-full px-3.5 py-1.5 text-xs font-black uppercase ${mode === "general" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-950" : "border border-slate-300 text-slate-500 dark:border-white/10 dark:text-zinc-400"}`}>
            General
          </button>
          <button onClick={() => setMode("specific")} data-testid="retailer-mode-specific" className={`rounded-full px-3.5 py-1.5 text-xs font-black uppercase ${mode === "specific" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-950" : "border border-slate-300 text-slate-500 dark:border-white/10 dark:text-zinc-400"}`}>
            Specific
          </button>
        </div>

        <form onSubmit={runSearch} className="mt-3 flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 dark:border-white/10 dark:bg-white/5">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${retailer.name}…`}
            data-testid="retailer-search-input"
            className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-slate-400 dark:text-white dark:placeholder:text-zinc-500"
          />
          <button type="submit" disabled={!query.trim() || loading} data-testid="retailer-search-submit" style={{ backgroundColor: retailer.primary }} className="shrink-0 rounded-lg px-4 py-2 text-xs font-black uppercase text-white disabled:opacity-40">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </button>
        </form>

        {mode === "specific" && (
          <div className="mt-3 grid grid-cols-2 gap-2" data-testid="retailer-specific-attrs">
            <input value={attrs.model} onChange={(e) => setAttrs({ ...attrs, model: e.target.value })} placeholder="Model" data-testid="retailer-attr-model" className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:text-white" />
            <input value={attrs.capacity} onChange={(e) => setAttrs({ ...attrs, capacity: e.target.value })} placeholder="Capacity / Size" data-testid="retailer-attr-capacity" className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:text-white" />
            <input value={attrs.condition} onChange={(e) => setAttrs({ ...attrs, condition: e.target.value })} placeholder="Condition" data-testid="retailer-attr-condition" className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:text-white" />
            <input value={attrs.color} onChange={(e) => setAttrs({ ...attrs, color: e.target.value })} placeholder="Color" data-testid="retailer-attr-color" className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:text-white" />
          </div>
        )}

        {error && <p className="mt-3 text-sm font-bold text-rose-500" data-testid="retailer-search-error">{error}</p>}
        {isDemo && <p className="mt-3 text-xs font-black uppercase tracking-wide text-amber-600 dark:text-amber-400" data-testid="retailer-demo-banner">Demo Data — live results are temporarily unavailable</p>}

        {results && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="retailer-results-grid">
            {results.length === 0 ? (
              <p className="col-span-full py-6 text-center text-sm font-bold text-slate-400" data-testid="retailer-no-results">No {retailer.name} listings matched — try a broader search.</p>
            ) : results.map((r) => {
              const meta = ROLE_META[r.role] || ROLE_META.BEST_PRICE;
              const badge = getTrustBadge((r.verdict || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", ""));
              return (
                <button key={r.id} onClick={() => openResult(r)} data-testid={`retailer-result-${r.role}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 text-left dark:border-white/10 dark:bg-white/5">
                  <span className={`inline-flex w-fit items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black uppercase ${meta.color} dark:bg-white/10`}>
                    <meta.Icon className="h-3.5 w-3.5" /> {meta.label}
                  </span>
                  <div className="mt-2 flex h-24 items-center justify-center overflow-hidden rounded-xl bg-slate-50 dark:bg-white/5">
                    {r.image ? <SafeImg src={r.image} alt={r.title} className="h-full w-full object-contain" iconClassName="h-6 w-6 text-slate-300" /> : <ShieldCheck className="h-8 w-8 text-slate-300" />}
                  </div>
                  <h4 className="mt-2 line-clamp-2 text-xs font-bold text-slate-900 dark:text-white">{r.title}</h4>
                  <p className="mt-1 text-lg font-black text-slate-900 dark:text-white">${Number(r.price || 0).toFixed(2)}</p>
                  <span className={`mt-1 inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-black uppercase ${badge.className}`}>
                    <badge.Icon className="h-3 w-3" /> {badge.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </main>

      <PdpModal deal={pdpDeal} open={!!pdpDeal} onClose={() => setPdpDeal(null)} currentTier={currentTier} location={user?.location} onLocked={() => navigate("/pricing")} onOpenPdp={setPdpDeal} isSaved={pdpDeal ? vaultIds.has(pdpDeal.id) : false} onToggleSave={() => guardedToggleSave(pdpDeal)} />
      <SignInModal open={showSignIn} onClose={() => setShowSignIn(false)} title="Sign in to unlock this" subtitle="Create a free account to save deals and turn on alerts." />
    </div>
  );
}
